import { useCallback, useEffect, useState } from 'react';

/**
 * Per-desk seat identity (#264 — pre-RBAC seat-claim).
 *
 * A desk names itself once ("Desk 2", "Duty Officer") and that name rides every
 * claim-acknowledge as the alert's `owner_label` — the FIRST desk to acknowledge
 * an urgent alert claims it, and every other desk's queue row flips to
 * "ACK — owned by [seat]" (see AnomalyQueuePanel + the anomaly-workstate claim
 * guard). Storage follows the pinnedFeeds idiom: localStorage is the durable
 * source of truth, a same-origin BroadcastChannel pushes live updates to sibling
 * windows (COP + /panel are the same desk), and a `storage` listener is the
 * belt-and-braces fallback. A window-local CustomEvent covers multiple hook
 * instances inside ONE window (queue header chip + Settings ▾ entry).
 *
 * HOW SEAT NAME RELATES TO operator_id — by design, it doesn't replace it:
 *   • `operator_id` (the `x-operator-id` header, defaulting to 'demo-operator')
 *     and `session_id` (lib/operatorActions) remain the AUDIT identity on every
 *     write — unchanged by this feature. Rewiring them to a free-text label
 *     would silently change audit semantics across every route; that is the
 *     RBAC phase's job.
 *   • The seat name is a parallel, human-chosen DESK label that only ever
 *     travels as `owner_label` (who is working this alert) and inside audit
 *     payloads. It is HONOR-SYSTEM: no auth, trivially editable, and labeled
 *     as such in the UI ("honor system · pre-RBAC").
 *   • Upgrade path (#190): RBAC replaces the free-text seat with authenticated
 *     identity as the owner source; `owner_label` and the claim guard are
 *     unchanged — only where the name comes from changes.
 */

export const SEAT_NAME_KEY = 'tsr.seatName';
export const SEAT_CHANNEL = 'tsr-seat';
/** sessionStorage flag — "Later" on the first-run prompt snoozes it for the
 *  browser session (a fresh app open re-nudges an unnamed desk exactly once). */
export const SEAT_PROMPT_DISMISSED_KEY = 'tsr.seatPromptDismissed';
/** Same-window update event (BroadcastChannel never echoes to its own window). */
export const SEAT_UPDATE_EVENT = 'tsr:seat-update';

/** Display-friendly ceiling, well under the server's owner_label max (120). */
export const SEAT_NAME_MAX = 40;

interface SeatMessage {
  type: 'seat:update';
  seat: string | null;
}

/** Trim + cap a raw seat entry; empty collapses to null (unset). */
export function normalizeSeatName(raw: string | null | undefined): string | null {
  const trimmed = (raw ?? '').trim().slice(0, SEAT_NAME_MAX).trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function getSeatName(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return normalizeSeatName(localStorage.getItem(SEAT_NAME_KEY));
  } catch {
    return null;
  }
}

export function setSeatName(raw: string | null): void {
  const seat = normalizeSeatName(raw);
  try {
    if (seat === null) localStorage.removeItem(SEAT_NAME_KEY);
    else localStorage.setItem(SEAT_NAME_KEY, seat);
  } catch {
    /* quota / private mode — broadcast still syncs live windows this session. */
  }
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(SEAT_CHANNEL);
    channel.postMessage({ type: 'seat:update', seat } satisfies SeatMessage);
    channel.close();
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SEAT_UPDATE_EVENT, { detail: { seat } }));
  }
}

export function isSeatPromptDismissed(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    return sessionStorage.getItem(SEAT_PROMPT_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissSeatPrompt(): void {
  try {
    sessionStorage.setItem(SEAT_PROMPT_DISMISSED_KEY, '1');
  } catch {
    /* ignore — the prompt just re-shows next load */
  }
}

/**
 * Live seat-name hook. Every instance (queue header chip, Settings ▾ entry,
 * sibling windows) converges on the same value: a set persists + broadcasts;
 * receivers replace state (idempotent, no echo loop).
 */
export function useSeatName(): readonly [string | null, (raw: string | null) => void] {
  const [seat, setSeat] = useState<string | null>(() => getSeatName());

  useEffect(() => {
    const channel =
      typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SEAT_CHANNEL);
    if (channel) {
      channel.onmessage = (e: MessageEvent) => {
        const data = e.data as SeatMessage | undefined;
        if (data?.type === 'seat:update') setSeat(normalizeSeatName(data.seat));
      };
    }
    const onLocal = (e: Event): void => {
      const seatDetail = (e as CustomEvent<{ seat?: string | null }>).detail?.seat ?? null;
      setSeat(normalizeSeatName(seatDetail));
    };
    const onStorage = (e: StorageEvent): void => {
      if (e.key === SEAT_NAME_KEY) setSeat(getSeatName());
    };
    window.addEventListener(SEAT_UPDATE_EVENT, onLocal);
    window.addEventListener('storage', onStorage);
    return () => {
      channel?.close();
      window.removeEventListener(SEAT_UPDATE_EVENT, onLocal);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const set = useCallback((raw: string | null) => {
    setSeatName(raw);
    setSeat(normalizeSeatName(raw));
  }, []);

  return [seat, set] as const;
}
